import { useState, useEffect, FormEvent, useRef } from 'react';
import { Link } from 'react-router-dom';
import { aboutInfo } from '@/mocks/about';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    setVisible(true);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9qoch7nahb7382ovcr0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, typeof v === 'string' ? v : ''])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (typeof msg === 'string' && (msg.includes('spam') || msg.includes('form data is spam'))) {
          setFormStatus('success');
          form.reset();
        } else {
          setFormStatus('error');
          setFormError(typeof msg === 'string' ? msg : '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '关于我们' },
  ];

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 高清无码指南',
    description: '了解高清无码指南团队，国内首个系统性聚焦高清视频编码与画质优化的专业技术内容平台。',
    url: 'https://hd-uncensored-guide.readdy.site/about',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>

      <div className="pt-20 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-5xl mx-auto">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <section
            className={`transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center mb-14 md:mb-20">
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-900 mb-4">关于我们</h1>
              <p className="text-sm text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                一群热爱视频技术的伙伴，致力于让每一位创作者都能呈现最优质的视觉体验
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 mb-14 md:mb-20 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">{aboutInfo.brandIntro}</p>
                <p className="text-sm text-foreground-700 leading-relaxed">{aboutInfo.mission}</p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20team%20collaboration%20in%20a%20warm%20lit%20studio%2C%20digital%20content%20creation%20atmosphere%2C%20minimal%20workspace%20with%20warm%20amber%20lighting%2C%20professional%20video%20production%20environment%2C%20cinematic%20composition%20with%20soft%20bokeh&width=700&height=450&seq=about-brand&orientation=landscape"
                  alt="高清无码指南团队"
                  title="高清无码指南团队 - 品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-14 md:mb-20">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-4 md:p-6 bg-background-100 rounded-lg">
                  <div className="font-heading text-2xl md:text-3xl text-accent-600 mb-1">{stat.value}</div>
                  <div className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="mb-14 md:mb-20">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">核心团队</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                {aboutInfo.team.map((member) => (
                  <div key={member.name} className="bg-background-100 border border-background-200/70 rounded-lg p-5 text-center">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-user-3-line text-2xl text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-base text-foreground-900 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-600 mb-2 whitespace-nowrap">{member.role}</p>
                    <p className="text-xs text-foreground-500 leading-relaxed">{member.bio}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-14 md:mb-20">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-8 text-center">读者口碑</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {aboutInfo.testimonials.map((t, idx) => (
                  <div key={idx} className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6">
                    <div className="flex items-center gap-1 mb-3 text-accent-500">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className="ri-star-fill text-sm"></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed mb-4">&ldquo;{t.content}&rdquo;</p>
                    <div className="border-t border-background-200/70 pt-3">
                      <p className="text-sm font-medium text-foreground-900">{t.author}</p>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact" className="max-w-2xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-3 text-center">联系我们</h2>
              <p className="text-sm text-foreground-600 text-center mb-8">
                有任何问题或合作意向，欢迎通过以下方式联系我们
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                  <p className="text-sm text-foreground-800">{aboutInfo.contact.email}</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">微信公众号</p>
                  <p className="text-sm text-foreground-800">{aboutInfo.contact.wechat}</p>
                </div>
                <div className="bg-background-100 border border-background-200/70 rounded-lg p-4 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <p className="text-xs text-foreground-500 mb-1">办公地址</p>
                  <p className="text-sm text-foreground-800">{aboutInfo.contact.address}</p>
                </div>
              </div>

              <form
                ref={formRef}
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-100 border border-background-200/70 rounded-lg p-5 md:p-6 space-y-4"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-3 py-2.5 border border-background-300 rounded-md text-sm bg-background-50 text-foreground-900 resize-none focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的留言（最多 500 字）"
                  ></textarea>
                </div>

                <div className="honeypot-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  发送留言
                </button>

                {formStatus === 'success' && (
                  <p className="text-sm text-accent-600 text-center">感谢您的留言，我们会尽快回复！</p>
                )}
                {formStatus === 'error' && (
                  <p className="text-sm text-red-600 text-center">{formError}</p>
                )}
              </form>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}